import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FaSpinner } from "react-icons/fa";
import Header from "../../component/Header";
import Background2 from "../../assets/Images/offerLetterBack.jpg";
import { updateApplication } from "../../redux/applicationList/applicationSlice";
import { addNotification } from "../../redux/notification/notificationSlice";
import PaymentHandler from "../../component/PaymentHandler";
import { useMakeRequest } from "../../hooks/useMakeRequest";
import { PAYMENT_API } from "../../resources/resource";

const fetchOfferURL = `${process.env.REACT_APP_API_URL}offer/getAnOffer`;
const updateOfferURL = `${process.env.REACT_APP_API_URL}offer/updateOffer`;

const Offer = () => {
  const { offerId, applicationId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const paymentTrigger = useRef(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offerData, setOfferData] = useState({});
  const [paymentMade, setPaymentMade] = useState(false);
  const user = useSelector((state) => state?.user?.currentUser);
  const makeRequest = useMakeRequest();

  useEffect(() => {
    const fetchOfferDetails = async () => {
      try {
        setFetchLoading(true);
        const { data } = await axios.post(fetchOfferURL, { offerId });
        setOfferData(data);
        setFetchLoading(false);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load offer details.");
        setFetchLoading(false);
      }
    };
    fetchOfferDetails();
  }, [offerId]);

  const handleOfferResponse = async (response) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.put(updateOfferURL, {
        status: response,
        offerId,
        sender: offerData.employer?._id,
        recipient: offerData.candidate?._id,
        application: applicationId,
      });
      dispatch(updateApplication(data.offer));
      dispatch(addNotification(data.notification));
      toast.success(`Offer ${response} successfully!`);
      navigate("/");
    } catch (err) {
      console.error(err);
      setError("Failed to update offer. Please try again.");
      toast.error("Error updating offer.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = () => {
    if (paymentTrigger.current) {
      paymentTrigger.current.triggerPayment(); // Trigger payment via ref
      toast.info("Kindly make payment to view details");
    }
  };

  const savePayment = ({ profile, reference }) => {
    console.log("Saving Payment:", { profile, reference }); // Debug
    makeRequest(
      `${PAYMENT_API}/createPayment`,
      "POST",
      { ...profile, reference },
      (data) => {
        console.log("Payment Saved:", data);
        toast.success("Payment recorded successfully!");
        setPaymentMade(true); // Update UI after saving
      },
      (error) => {
        console.error("Payment Error:", error);
        toast.error("Failed to save payment.");
      },
      () => console.log("Request Complete")
    );
  };

  if (fetchLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <FaSpinner className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 pt-16 sm:pt-20">
      <Header />
      <main className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="relative w-full max-w-3xl bg-white rounded-lg shadow-md overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{ backgroundImage: `url(${Background2})` }}
          />
          <div className="relative z-10 p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 text-center mb-6">
              Resumption Confirmation
            </h1>
            <div className="text-right text-sm text-gray-600 mb-6">
              <p>
                {offerData?.candidate?.firstName}{" "}
                {offerData?.candidate?.lastName}
              </p>
              <p>
                {offerData?.candidate?.localGovernment},{" "}
                {offerData?.candidate?.state}
              </p>
              <p>{offerData?.candidate?.country}</p>
              <p>{offerData?.candidate?.phoneNumber}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Resumption Time
                </label>
                <p className="mt-1 text-gray-900">
                  {paymentMade ? (
                    offerData?.resumptionTime
                  ) : (
                    <span
                      className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
                      onClick={handlePayment}
                    >
                      Make Payment
                    </span>
                  )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Resumption Date
                </label>
                <p className="mt-1 text-gray-900">
                  {paymentMade ? (
                    offerData?.resumptionDate
                  ) : (
                    <span
                      className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
                      onClick={handlePayment}
                    >
                      Make Payment
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="space-y-4 text-gray-700 mb-6">
              <p>Dear {offerData?.candidate?.firstName},</p>
              <p>
                Following a successful interview with{" "}
                <span className="font-medium">
                  {offerData?.employer?.companyName}
                </span>
                , we are pleased to offer you a position. Your qualifications
                align perfectly with our needs.
              </p>
              <p>
                You are expected to resume on{" "}
                <span className="underline">
                  {paymentMade ? (
                    offerData?.resumptionDate
                  ) : (
                    <span
                      className="text-blue-600 hover:text-blue-800 cursor-pointer"
                      onClick={handlePayment}
                    >
                      Make Payment
                    </span>
                  )}
                </span>{" "}
                by{" "}
                <span className="underline">
                  {paymentMade ? (
                    offerData?.resumptionTime
                  ) : (
                    <span
                      className="text-blue-600 hover:text-blue-800 cursor-pointer"
                      onClick={handlePayment}
                    >
                      Make Payment
                    </span>
                  )}
                </span>
                . Prompt attendance is essential.
              </p>
              <p>
                Your monthly salary will be{" "}
                <span className="underline">
                  N {offerData?.amount || "N/A"}
                </span>
                , with a review after a 3-6 month probation period based on
                performance.
              </p>
              <p>
                For questions, contact us at{" "}
                {offerData?.employer?.phoneNumber || "the number below"}. We’re
                excited to welcome you!
              </p>
            </div>
            <div className="text-sm text-gray-600 mb-6">
              <p className="font-medium">Sincerely,</p>
              <p className="font-semibold">
                {offerData?.employer?.companyName}
              </p>
              <p>
                {offerData?.employer?.localGovernment},{" "}
                {offerData?.employer?.country}
              </p>
            </div>
            {paymentMade ? (
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <button
                  onClick={() => handleOfferResponse("Accepted")}
                  disabled={loading}
                  className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {loading && loading === "Accepted" ? (
                    <FaSpinner className="animate-spin mr-2" />
                  ) : null}
                  Accept
                </button>
                <button
                  onClick={() => handleOfferResponse("Rejected")}
                  disabled={loading}
                  className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  {loading && loading === "Rejected" ? (
                    <FaSpinner className="animate-spin mr-2" />
                  ) : null}
                  Reject
                </button>
              </div>
            ) : (
              <div className="text-center">
                <PaymentHandler
                  email={user?.email}
                  amount={"1000"} // Use offer amount or fallback
                  change={() => setPaymentMade(true)}
                  item="Offer Letter"
                  type="offer Acceptance"
                  profileHandler={savePayment}
                  profile={{ ...user, offerId, applicationId }} // Include relevant data
                  ref={paymentTrigger}
                />
              </div>
            )}
            {error && <p className="text-red-600 text-center mt-4">{error}</p>}
            <div className="text-center mt-6">
              <Link
                to="/"
                className="inline-block px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Offer;
